import * as React from "react";
import { cn } from "@/lib/utils";
import { X } from "lucide-react";

interface SkillTagProps extends React.HTMLAttributes<HTMLDivElement> {
  label: string;
  variant?: "primary" | "secondary" | "outline";
  size?: "sm" | "md" | "lg";
  removable?: boolean;
  onRemove?: () => void;
  icon?: React.ReactNode;
}

const SkillTag = React.forwardRef<HTMLDivElement, SkillTagProps>(
  (
    {
      className,
      label,
      variant = "primary",
      size = "md",
      removable = false,
      onRemove,
      icon,
      ...props
    },
    ref
  ) => {
    const variantClasses = {
      primary: "skill-tag",
      secondary: "skill-tag-secondary",
      outline:
        "bg-transparent border border-[hsl(var(--color-3))] text-[hsl(var(--color-7))]",
    };

    const sizeClasses = {
      sm: "px-2 py-0.5 text-xs",
      md: "px-3 py-1 text-sm",
      lg: "px-4 py-1.5 text-base",
    };

    return (
      <div
        ref={ref}
        className={cn(
          "inline-flex items-center gap-1.5 rounded-full font-medium transition-all duration-200",
          variantClasses[variant],
          sizeClasses[size],
          removable && "pr-1.5",
          className
        )}
        {...props}
      >
        {icon && <span className="flex-shrink-0">{icon}</span>}
        <span className="truncate">{label}</span>
        {removable && onRemove && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
            className={cn(
              "flex-shrink-0 rounded-full p-0.5 transition-colors",
              variant === "primary"
                ? "hover:bg-[hsl(var(--color-4))]/20"
                : "hover:bg-gray-200"
            )}
          >
            <X className="h-3 w-3" />
          </button>
        )}
      </div>
    );
  }
);
SkillTag.displayName = "SkillTag";

interface SkillTagGroupProps extends React.HTMLAttributes<HTMLDivElement> {
  skills: string[];
  variant?: "primary" | "secondary" | "outline";
  size?: "sm" | "md" | "lg";
  maxVisible?: number;
  removable?: boolean;
  onRemove?: (skill: string) => void;
}

const SkillTagGroup = React.forwardRef<HTMLDivElement, SkillTagGroupProps>(
  (
    {
      className,
      skills,
      variant = "primary",
      size = "md",
      maxVisible,
      removable = false,
      onRemove,
      ...props
    },
    ref
  ) => {
    const visibleSkills = maxVisible ? skills.slice(0, maxVisible) : skills;
    const remainingCount = maxVisible ? skills.length - maxVisible : 0;

    return (
      <div
        ref={ref}
        className={cn("flex flex-wrap gap-2", className)}
        {...props}
      >
        {visibleSkills.map((skill) => (
          <SkillTag
            key={skill}
            label={skill}
            variant={variant}
            size={size}
            removable={removable}
            onRemove={onRemove ? () => onRemove(skill) : undefined}
          />
        ))}
        {remainingCount > 0 && (
          <SkillTag
            label={`+${remainingCount} more`}
            variant="outline"
            size={size}
          />
        )}
      </div>
    );
  }
);
SkillTagGroup.displayName = "SkillTagGroup";

export { SkillTag, SkillTagGroup };
export type { SkillTagProps, SkillTagGroupProps };
